import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "config/firebase";

const AUTOMATIONS_COLLECTION = "automations";
const RUNS_COLLECTION = "automation_runs";

/**
 * Fetch all automations ordered by createdAt desc
 */
export const getAutomationsList = async () => {
  try {
    const q = query(
      collection(db, AUTOMATIONS_COLLECTION),
      orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);
    return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching automations:", error);
    return [];
  }
};

/**
 * Fetch an automation by ID
 */
export const getAutomationById = async (id) => {
  try {
    const docRef = doc(db, AUTOMATIONS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  } catch (error) {
    console.error("Error fetching automation by ID:", error);
    return null;
  }
};

/**
 * Create a new automation
 */
export const createAutomation = async (automationData) => {
  try {
    const docRef = await addDoc(collection(db, AUTOMATIONS_COLLECTION), {
      ...automationData,
      status: automationData.status || "published", // "published" | "paused" | "draft"
      createdAt: Date.now(),
      updatedAt: Date.now(),
      lastRunAt: null,
      lastRunStatus: null,
    });
    return docRef.id;
  } catch (error) {
    console.error("Error creating automation:", error);
    throw error;
  }
};

/**
 * Update an existing automation
 */
export const updateAutomation = async (id, updatedFields) => {
  try {
    const docRef = doc(db, AUTOMATIONS_COLLECTION, id);
    await updateDoc(docRef, {
      ...updatedFields,
      updatedAt: Date.now(),
    });
    return true;
  } catch (error) {
    console.error("Error updating automation:", error);
    throw error;
  }
};

/**
 * Delete an automation
 */
export const deleteAutomation = async (id) => {
  try {
    const docRef = doc(db, AUTOMATIONS_COLLECTION, id);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error("Error deleting automation:", error);
    throw error;
  }
};

/**
 * Fetch recent execution runs
 */
export const getRecentRuns = async (max = 20) => {
  try {
    const q = query(
      collection(db, RUNS_COLLECTION),
      orderBy("startedAt", "desc"),
      limit(max)
    );
    const snap = await getDocs(q);
    return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching runs:", error);
    return [];
  }
};

/**
 * Trigger manual execution of an automation
 */
export const executePrognosAutomationRemote = async (automationId, inlineConfig = null) => {
  try {
    const runCallable = httpsCallable(functions, "executePrognosAutomation");
    const result = await runCallable({
      automationId,
      config: inlineConfig,
    });
    return result.data;
  } catch (error) {
    console.error("Error executing automation callable:", error);
    throw error;
  }
};

/**
 * Generate automation via AI prompt
 */
export const generateAutomationWithAIRemote = async (prompt) => {
  try {
    const aiCallable = httpsCallable(functions, "generateAutomationWithAI");
    const result = await aiCallable({ prompt });
    return result.data;
  } catch (error) {
    console.error("Error generating automation via AI callable:", error);
    throw error;
  }
};
